import {
  ChangeDetectorRef, ChangeDetectionStrategy,
  Component, Input, OnInit, ViewChild, ViewChildren, QueryList,
  Output, EventEmitter,
} from '@angular/core';
import { Observable ,  Subscription } from 'rxjs';
import * as _ from 'lodash';

import { prettifyNumber } from 'utils/prettify';
import { BaseModalComponent } from '../../modal/_base-modal.component';
import { SubscriptionService } from '../../subscription/subscription.service';
import { PlanComponent } from './plan/plan.component';
import { LoaderComponent } from '../../loader/loader.component';
import { DefaultModalComponent } from '../default-modal/default-modal.component';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';
import {takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'change-plan-modal',
  templateUrl: 'change-plan-modal.component.html',
  styleUrls: ['change-plan-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePlanModalComponent extends BaseModalComponent {

  // settings
  bundleUsersCount: number = 3;
  baseUsersCount: number = 6;
  minSystemUsersCount: number = this.baseUsersCount;
  maxSystemUsersCount: number = this.bundleUsersCount * 33;

  plans: Array<any>;
  currPlan: any;
  currPeriod: string = 'month';
  suggestPlanIndex: number;

  // curr values
  currUsersCount: number;
  currMaxUsersCount: number;

  minUsersCount: number = this.minSystemUsersCount;
  desiredUsersCount: number = this.minSystemUsersCount;
  maxUsersCount: number = this.maxSystemUsersCount;
  isUpgraded: boolean = false;
  isUserCountChanged: boolean = false;

  currItemsCount: number;

  @Input() discountInfo: any;
  @Input() initialUsersCount: number;
  @Input() brandId: string;

  @Output() closed = new EventEmitter();
  @Output() upgradePlan = new EventEmitter();

  @ViewChildren(PlanComponent) planComps: QueryList<PlanComponent>;

  @ViewChild('changePlanModalLoader', {static: false}) loader: LoaderComponent | void;

  @ViewChild('plansModal', {static: false})
    protected declare defaultModal: DefaultModalComponent | void;

  @ViewChild('usersWarningModal', {static: false})
    protected usersWarningModal: DefaultModalComponent | void;

  constructor(
    private ref: ChangeDetectorRef,
    private subscriptionService: SubscriptionService,
  ) {
    super();
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

   get filteredPlans(): Array<any> {
     if (!this.plans || !this.plans.length) { return null; }

     // filter month/annual plans
     // @ts-ignore
      const periodPlans = _.filter(this.plans, {interval: this.currPeriod});


     if (this.currPlan) {
       if (this.isHighestPlan(this.currPlan)) { // highest plan - return current
         return [this.currPlan];
       } else {
         const nextPlan = this.getPlanById(this.currPlan.next_upgrade);
         if (nextPlan) {
           // need to return current && next
           return [this.currPlan, nextPlan];
         } else {
           // need to return current
           return [this.currPlan];
         }
       }
     } else { // if no current plan
       if (this.suggestedCustomPlan) {
         // if custom plan id contains brandId - suggest it
         return [this.suggestedCustomPlan];
       } else if (this.hasSuggestedPlan) {
         // find suitable plans by items count
         const suggestPlan = this.plans[this.suggestPlanIndex];
         const suggestPlans = [suggestPlan];

         const nextPlan = this.getPlanById(suggestPlan.next_upgrade);

         if (nextPlan) { suggestPlans.push(nextPlan); }

         return suggestPlans;
       }
     }

     return [];
   }

   get isCurrLowestPlan(): boolean {
     if (!this.plans || !this.plans.length) { return false; }

     if (!_.isNil(this.currPlan)) { return this.isLowestPlan(this.currPlan); }

     if (this.hasSuggestedPlan) { return this.isLowestPlan(this.plans[this.suggestPlanIndex]); }

     return false;
   }

   get isCurrHighestPlan(): boolean {
     if (!this.plans || !this.plans.length) { return false; }

     if (!_.isNil(this.currPlan)) { return this.isHighestPlan(this.currPlan); }

     if (this.hasSuggestedPlan) { return this.isHighestPlan(this.plans[this.suggestPlanIndex]); }

     return false;
   }

   get canMinusUsers() {
     return (this.desiredUsersCount > this.minUsersCount &&
       (this.desiredUsersCount - this.bundleUsersCount) >= this.currUsersCount);
   }

   get canPlusUsers() {
     return this.desiredUsersCount < this.maxUsersCount;
   }

   get currPlanPrice(): number {
      if (!this.currPlan) { return 0; }
      return this.currPlan.amount;
   }

   get usersCost(): number {
     return Math.max(0, this.desiredUsersCount - this.baseUsersCount) / this.bundleUsersCount * 50;
   }

   get totalPrice(): number {
     return this.subTotal - this.discountAmount;
   }

   get totalPriceFormatted(): String {
     return prettifyNumber(this.totalPrice.toFixed(2), ',', 2);
   }

   get subTotal(): number {
     if (this.selectedPlan) {
       return this.selectedPlan.amount + this.usersCost;
     } else if (this.hasSuggestedPlan && this.plans) {
       return this.plans[this.suggestPlanIndex].amount + this.usersCost;
     }
     return this.currPlanPrice + this.usersCost;
   }

   get hasChanges(): boolean {
     // if we have custom plan to suggest
     if (this.suggestedCustomPlan) { return true; }

    // if we have regular plan to suggest
     if (this.hasSuggestedPlan) {
       return !this.isCurrHighestPlan;
     }

    // if we have selected plan
     if (this.selectedPlan) { return true; }

     return (this.desiredUsersCount !== this.currMaxUsersCount) && this.isUserCountChanged;
   }

   get selectedPlan(): any {
     // @ts-ignore
       return _.find(this.plans, {is_selected: true});
   }

   get hasSuggestedPlan(): boolean {
     return _.isNumber(this.suggestPlanIndex);
   }

   get suggestedCustomPlan(): any {
     return _.find(this.plans, (plan: any) => {
       return (plan.custom_plan && `${plan.id}`.indexOf(this.brandId) >= 0);
     });
   }

   get isDesiredIsMinSystem(): boolean {
     return this.desiredUsersCount === this.minSystemUsersCount;
   }

   get discountAmount(): number {
     let discount;
     if (this.isDiscountAmount) {
       discount = _.get(this.discountInfo, 'amount_off');
     } else if (this.isDiscountPercent) {
       discount = this.subTotal * (_.get(this.discountInfo, 'percent_off') / 100);
     }
     return discount ? discount : 0;
   }

   get discountAmountFormatted(): String {
     if (this.isDiscountPercent) { return `${this.discountAmount}`; }
     return prettifyNumber(this.discountAmount.toFixed(2), ',', 2);
   }

   get discountMonths(): number {
     return _.get(this.discountInfo, 'duration_in_months');
   }

   get isDiscountPercent(): boolean {
     return _.has(this.discountInfo, 'percent_off') && !!this.discountInfo.percent_off;
   }

   get isDiscountAmount(): boolean {
     return _.has(this.discountInfo, 'amount_off') && !!this.discountInfo.amount_off;
   }

   get isDiscountUsd(): boolean {
     return _.get(this.discountInfo, 'currency') === 'usd';
   }

   get discountLabel(): string {
     return _.get(this.discountInfo, 'id', 'Discount');
   }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

   public show() {
     this.clearData();
     this.refreshInfo();
     super.show();
   }

   public showLoader() {
     if (this.loader instanceof LoaderComponent) {
       this.loader.show();
     }
     this.ref.markForCheck();
   }

   public hideLoader() {
     if (this.loader instanceof LoaderComponent) {
       this.loader.hide();
     }
     this.ref.markForCheck();
   }

   public setUpgraded() {
     this.isUpgraded = true;
     this.ref.markForCheck();
   }

   public showUsersWarning() {
     if (this.usersWarningModal instanceof DefaultModalComponent) {
       this.usersWarningModal.show();
     }
   }

   public hideUsersWarning() {
     if (this.usersWarningModal instanceof DefaultModalComponent) {
       this.usersWarningModal.hide();
     }
   }

   public formatPrice(price: number): string {
     return price.toFixed(2);
   }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onBtnMinusClick() {
    this.desiredUsersCount -= this.bundleUsersCount;
    this.isUserCountChanged = true;
  }

  onBtnPlusClick() {
    this.desiredUsersCount += this.bundleUsersCount;
    this.isUserCountChanged = true;
  }

  onUpgradeBtnClick() {
    let planId;
    if (this.selectedPlan) {
      planId = this.selectedPlan.id;
    } else if (this.hasSuggestedPlan) {
      planId = this.plans[this.suggestPlanIndex].id;
    } else if (this.currPlan) {
      planId = this.currPlan.id;
    }

    this.upgradePlan.emit({planId, usersCount: this.desiredUsersCount});
  }

  onSelectedPlanClicked(plan: any) {
    this.selectPlan(plan, true);
  }

  onUnselectedPlanClicked(plan: any) {
    this.selectPlan(plan, false);
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  private refreshInfo() {
    this.showLoader();
    forkJoin([
      this.subscriptionService.getMonthlyPlansInfo(this.brandId),
      this.subscriptionService.getAddonInfo(this.brandId),
      this.subscriptionService.getUsersCountInfo(this.brandId),
      this.subscriptionService.getPlanItemsCount(this.brandId),
    ])
    .pipe(takeWhile(() => this.isActive))
    .subscribe((result: Array<any>) => {
      const plans = result[0];
      const addonsInfo = result[1];
      const usersInfo = result[2];
      const itemsInfo = result[3];

      // order by minimum items count
      this.plans = this.orderPlans(plans);

      _.forEach(this.plans, (plan: any) => {
        plan.is_selected = false;
      });

      // plans handling
      // @ts-ignore
        this.currPlan = _.find(this.plans, {current_subscription: true});

      // users handling
      this.currUsersCount = usersInfo.users;
      this.currMaxUsersCount = usersInfo.max_users;
      this.updateUsersSettings(usersInfo, _.find(addonsInfo, {id: 'user-addon'}));

      // items handling
      this.currItemsCount = itemsInfo.items;

      this.updateSuggestPlanIndex();

      this.hideLoader();
      this.ref.markForCheck();
    });
  }

  private getPlanById(id: string) {
    // @ts-ignore
      const plan = _.find(this.plans, {id});
    return plan ? plan : null;
  }

  private updateSuggestPlanIndex() {
    if (_.isNumber(this.currItemsCount) && !this.currPlan) {
      this.suggestPlanIndex = _.findIndex(this.plans, (plan: any) => {
        return Number(plan.max_items) > this.currItemsCount;
      });
      if (this.suggestPlanIndex < 0) {
        this.suggestPlanIndex = this.plans.length - 1;
      }
    } else {
      this.suggestPlanIndex = undefined;
    }

    this.ref.markForCheck();
  }

  private selectPlan(selectPlan: any, value: boolean = true) {
    _.forEach(this.plans, (plan: any) => {
      if (selectPlan && plan.id === selectPlan.id) {
        plan.is_selected = value;
      } else {
        plan.is_selected = false;
      }
    });
    this.ref.markForCheck();
  }

  private updateUsersSettings(usersInfo: any, addonInfo: any) {
    // set min users value
    this.minUsersCount = this.minSystemUsersCount;
    if (addonInfo && !_.isNil(addonInfo.max_quantity)) {
      this.maxUsersCount = addonInfo.max_quantity;
    }
    while (this.minUsersCount < usersInfo.users && this.minUsersCount + this.bundleUsersCount <= this.maxUsersCount) {
      this.minUsersCount += this.bundleUsersCount;
    }

    // set current users value
    if (this.initialUsersCount)  {
      this.desiredUsersCount = this.initialUsersCount;
    } else {
      this.desiredUsersCount = usersInfo.max_users > this.minUsersCount ? usersInfo.max_users : this.minUsersCount;
    }
  }

  private orderPlans(plans: any) {
    return _.orderBy(plans, (plan: any) => Number(plan.min_items));
  }

  private isSuggestedPlan(plan: any): boolean {
    if (!this.hasSuggestedPlan) { return false; }
    return this.plans[this.suggestPlanIndex] === plan;
  }

  private isSuggestedCustomPlan(plan: any): boolean {
    return this.suggestedCustomPlan === plan;
  }

  private getDowngradePlan(): any {
    if (this.currPlan) {
      const downgradeId = _.get(this.currPlan, 'next_downgrade');
      return !_.isNil(downgradeId) ? this.getPlanById(downgradeId) : null;
    }
    if (this.hasSuggestedPlan) {
      const downgradeId = _.get(this.plans[this.suggestPlanIndex], 'next_downgrade');
      return !_.isNil(downgradeId) ? this.getPlanById(downgradeId) : null;
    }
    return null;
  }


  private isLowestPlan(plan: any): boolean {
    return (plan && _.isNil(plan.next_downgrade));
  }

  private isHighestPlan(plan: any): boolean {
    return (plan && _.isNil(plan.next_upgrade));
  }

  private clearData() {
    this.plans = undefined;
    this.currPlan = undefined;
    this.suggestPlanIndex = 0;
    this.currUsersCount = undefined;
    this.minUsersCount = this.minSystemUsersCount;
    this.maxUsersCount = this.maxSystemUsersCount;
    this.desiredUsersCount = this.minSystemUsersCount;
    this.isUpgraded = false;
    this.isUserCountChanged = false;
    this.currItemsCount = undefined;
    this.hideLoader();
  }
}
